import { useCallback, useEffect, useState } from 'react';
import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useScript from 'hooks/useScript';
import { useTracking } from '@podiumhq/tracking';
import { createCookie } from '../../lib/getIdToken';
import env from 'env';
import { Box } from '@podiumhq/design-system';
import { buttonContainerStyles } from './styles';

const SignInWithApple = ({ onLogin }) => {
  const {
    checkEmailAddressExists,
    checkForInvite,
    email,
    setEmail,
    setFirstName,
    setLastName,
    setThirdPartyJwt,
  } = useTrialsContext() || {};
  const { trackEvent } = useTracking();
  const [refresh, setRefresh] = useState(0);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const status = useScript({ src: env.APPLE_AUTH_URI });

  const handleLogin = useCallback(
    async (event) => {
      const {
        authorization: { id_token: idToken },
      } = event.detail;
      document.cookie = createCookie({
        cookieName: 'aToken',
        cookieValue: idToken,
        hostname: window.location.hostname,
      });
      setThirdPartyJwt({ source: 'apple', jwt: idToken });
      const tokenParts = idToken.split('.');
      const token = atob(tokenParts[1]);
      const { email } = JSON.parse(token);
      await checkForInvite(email);
      checkEmailAddressExists(email)
        .then((exists) => {
          if (exists) {
            trackEvent('signup.action', {
              type: 'appleLoginRedirectExisting',
            });
            window.top.location.assign(env.ACCOUNTS_URL);
          } else {
            setEmail(email);
            if (event.detail?.user) {
              const {
                name: { firstName, lastName },
              } = event.detail.user;
              setFirstName(firstName);
              setLastName(lastName);
            }
            setLoginSuccess(true);
          }
        })
        .catch((error) => {
          trackEvent('signup.error', {
            type: 'appleLoginError',
            error: JSON.stringify(error),
          });
        });
    },
    [
      trackEvent,
      setThirdPartyJwt,
      checkEmailAddressExists,
      checkForInvite,
      setEmail,
      setFirstName,
      setLastName,
      setLoginSuccess,
    ]
  );

  useEffect(() => {
    document.addEventListener('AppleIDSignInOnSuccess', handleLogin);
    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', handleLogin);
    };
  }, [handleLogin]);

  useEffect(() => {
    if (!!env.APPLE_CLIENT_ID && !!env.APPLE_AUTH_URI) {
      setEnabled(true);
    }
  }, []);

  useEffect(() => {
    if (loaded) return;
    if (status.loaded && !status.error) {
      setLoaded(true);
    }
  }, [status, loaded]);

  useEffect(() => {
    if (loginSuccess && !!email) {
      trackEvent('signup.action', {
        type: 'appleLoginSuccess',
      });
      onLogin();
    }
  }, [loginSuccess, email, onLogin, trackEvent]);

  useEffect(() => {
    if (loaded && enabled) {
      if (!!window?.AppleID?.auth) {
        try {
          window.AppleID.auth.init({
            clientId: env.APPLE_CLIENT_ID,
            scope: 'name email',
            redirectURI: env.CHECKOUT_URL,
            usePopup: true,
          });
        } catch (error) {
          trackEvent('signup.action', {
            type: 'appleLoginFailure',
            error: JSON.stringify(error),
          });
        }
      } else {
        setTimeout(() => {
          setRefresh(refresh + 1);
        }, 250);
      }
    }
  }, [loaded, refresh, enabled, handleLogin, trackEvent]);

  return (
    enabled && (
      <Box
        id="appleid-signin"
        data-color="white"
        data-height="34"
        data-width="348"
        data-type="continue"
        data-mode="left-align"
        data-logo-size="large"
        data-logo-position="12"
        data-label-position="113"
        data-border="false"
        {...buttonContainerStyles}
      ></Box>
    )
  );
};

export default SignInWithApple;
